import * as React from "react";
import { Grid, Box, Alert } from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import { Trans } from "@lingui/macro";
import TextFieldAtom from "@atoms/TextField";
import IClientCreateOrganism, { IFormValues } from "./interface";
import createClientSchema from "@validation/createClient";
import { FormProvider, useForm } from "react-hook-form";
import { useMutation } from "react-query";
import { toast } from "react-toastify";
import { fetchFilter } from "@stores/reducers/filtersReducer";
import { useAppDispatch } from "@stores/hooks";
import PartnersService from "@services/PartnersService";
import ButtonAtom from "@atoms/Button";

/**
 * Including a double-layout mode ('patch' and 'normal')
 * @param mode
 * @returns
 */
const PartnerCreateOrganism = ({
  mode,
  client,
  onNewClient,
  onUpdate,
  hideAttributes,
}: IClientCreateOrganism) => {
  const [sErrors, setSErrors] = React.useState(null);
  const [msg, setMsg] = React.useState("");
  const [success, setSuccess] = React.useState<boolean>(false);

  const dispatch = useAppDispatch();

  const formInstance = useForm<IFormValues>({
    defaultValues: {
      name: client ? (client.name ? client.name : undefined) : "",
      email: client ? (client.email ? client.email : undefined) : "",
      phone: client ? (client.phone ? client.phone : undefined) : "",
    },
    mode: "onChange",
    reValidateMode: "onSubmit",
    resolver: yupResolver(createClientSchema),
  });

  const { mutate: createAssetMutation, isLoading } = useMutation(
    (formValues: IFormValues) =>
      (mode === "patch" && client
        ? PartnersService.put(formValues, client.id)
        : PartnersService.create(formValues)
      ).then((res: any) => {
        setMsg(res.getMsgString());
        if (!res.hasErrors()) {
          dispatch(fetchFilter("customers"));
          setSuccess(true);
          toast(res.getMsgString(), {
            type: "success",
          });
          if (onNewClient && !client) {
            onNewClient(res.getData());
          }
          if (onUpdate && client) {
            onUpdate(res.getData());
          }
        } else {
          setSErrors(res.getErrors());
          toast(res.getMsgString(), {
            type: "error",
          });
        }
      }),
  );

  const {
    formState: { isValid },
    handleSubmit: handleHookSubmit,
  } = formInstance;

  const handleSubmit = (data: IFormValues) => {
    const values = { ...data };
    createAssetMutation(values);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <form noValidate onSubmit={handleHookSubmit(handleSubmit)}>
        <FormProvider {...formInstance}>
          {success ? (
            <React.Fragment>
              {msg ? (
                <Box mt={2}>
                  <Alert severity="success">{msg}</Alert>
                </Box>
              ) : null}
            </React.Fragment>
          ) : (
            <React.Fragment>
              <Box mt={1}>
                <Grid container spacing={2}>
                  <Grid item md={6}>
                    <TextFieldAtom
                      controlName={"name"}
                      required={true}
                      variant={"outlined"}
                      label={<Trans>Name</Trans>}
                    />
                    {sErrors && sErrors["name"] ? (
                      <Alert severity="error" icon={false}>
                        {sErrors["name"]}
                      </Alert>
                    ) : null}
                  </Grid>
                  <Grid item xs={12} sm={12} md={6}>
                    <TextFieldAtom
                      controlName={"email"}
                      required={true}
                      variant={"outlined"}
                      label={<Trans>Email</Trans>}
                    />
                    {sErrors && sErrors["email"] ? (
                      <Alert severity="error" icon={false}>
                        {sErrors["email"]}
                      </Alert>
                    ) : null}
                  </Grid>
                </Grid>
                <Box mt={2}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={12} md={6}>
                      <TextFieldAtom
                        controlName={"phone"}
                        variant={"outlined"}
                        label={<Trans>Phone</Trans>}
                      />
                      {sErrors && sErrors["phone"] ? (
                        <Alert severity="error" icon={false}>
                          {sErrors["phone"]}
                        </Alert>
                      ) : null}
                    </Grid>
                    <Grid item xs={12} sm={12} md={6}></Grid>
                  </Grid>
                </Box>
              </Box>
              <Box mt={2}>
                {sErrors ? <Alert severity="error">{msg}</Alert> : null}
                <Box mt={2} sx={{ textAlign: "right" }}>
                  <ButtonAtom
                    type="submit"
                    disabled={!isValid}
                    loading={isLoading}
                    label={
                      client ? (
                        <Trans>Update provider</Trans>
                      ) : (
                        <Trans>Create provider</Trans>
                      )
                    }
                  />
                </Box>
              </Box>
            </React.Fragment>
          )}
        </FormProvider>
      </form>
    </Box>
  );
};

export default PartnerCreateOrganism;
